import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import SearchBar from "@/components/base/SearchBar";
import DramaListCard from "@/components/base/DramaListCard";
import EmptyState from "@/components/base/EmptyState";
import { allDramas, searchDramas } from "@/lib/drama";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const HOT_KEYWORDS = ["嫂子的职业", "职场", "都市", "悬疑", "情感", "香港"];

export default function Search() {
  const [params] = useSearchParams();
  const keyword = (params.get("q") || "").trim();
  const results = searchDramas(keyword);
  const hotDramas = [...allDramas].sort((a, b) => b.heat - a.heat).slice(0, 4);

  useDocumentMeta({
    title: `搜索结果：“${keyword || "全部"}” - 嫂子的职业在线观看`,
    description: keyword
      ? `与“${keyword}”相关的电视剧搜索结果，共 ${results.length} 部。嫂子的职业在线观看为你整理剧集资料与演职信息。`
      : "输入关键词搜索电视剧，支持剧名、演员、题材与标签匹配。",
    keywords: `${keyword || "电视剧"},搜索结果,电视剧在线观看,高清电视剧`,
  });

  return (
    <div className="min-h-screen bg-background-100">
      <SiteHeader />
      <main className="pt-16 md:pt-[72px]">
        <div className="border-b border-background-200 bg-background-50">
          <div className="mx-auto w-full max-w-[1280px] px-4 py-7 md:px-8 md:py-9">
            <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
              <Link to="/" className="transition-colors hover:text-primary-600">
                首页
              </Link>
              <i className="ri-arrow-right-s-line" aria-hidden="true" />
              <span className="text-foreground-800">搜索结果</span>
            </nav>
            <h1 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              {keyword ? `搜索结果：“${keyword}”` : "搜索电视剧"}
            </h1>
            <p className="mt-2 text-sm text-foreground-600">
              {keyword ? (
                <>
                  共找到 <span className="font-semibold text-primary-700">{results.length}</span> 部相关剧集
                </>
              ) : (
                "输入剧名、演员或题材关键词开始搜索"
              )}
            </p>

            <div className="mt-5 max-w-2xl">
              <SearchBar key={keyword} initialValue={keyword} variant="page" className="bg-background-50" />
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="text-xs text-foreground-500">热门搜索：</span>
              {HOT_KEYWORDS.map((item) => (
                <Link
                  key={item}
                  to={`/search?q=${encodeURIComponent(item)}`}
                  className={`rounded-full px-2.5 py-1 text-xs transition-colors ${
                    item === keyword
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-700 hover:bg-background-200"
                  }`}
                >
                  {item}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-8 md:py-8">
          {results.length > 0 ? (
            <div className="grid gap-3">
              {results.map((drama) => (
                <DramaListCard key={drama.id} drama={drama} />
              ))}
            </div>
          ) : (
            <div className="space-y-8">
              <EmptyState
                icon="ri-search-line"
                title={keyword ? "没有找到符合条件的电视剧" : "还没有输入搜索关键词"}
                description={
                  keyword
                    ? `没有与“${keyword}”匹配的剧集，换个关键词试试，或者浏览分类页看看。`
                    : "你可以在上方搜索框输入剧名、演员或题材，例如“嫂子的职业”“职场”“悬疑”。"
                }
                action={
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <Link
                      to="/category"
                      className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                    >
                      <i className="ri-list-check-2" aria-hidden="true" />
                      浏览全部分类
                    </Link>
                    <Link
                      to="/"
                      className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
                    >
                      返回首页
                    </Link>
                  </div>
                }
              />

              <section aria-labelledby="hot-search-title">
                <h2 id="hot-search-title" className="font-heading text-lg font-bold text-foreground-950">
                  大家都在看
                </h2>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  {hotDramas.map((drama) => (
                    <Link
                      key={drama.id}
                      to={`/detail/${drama.id}`}
                      className="group flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300"
                    >
                      <span className="h-16 w-11 shrink-0 overflow-hidden rounded bg-primary-900">
                        <img
                          src={drama.cover}
                          alt={drama.title}
                          title={`${drama.title} 高清电视剧在线观看`}
                          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium text-foreground-900 group-hover:text-primary-600">
                          {drama.title}
                        </span>
                        <span className="mt-1 block truncate text-xs text-foreground-500">
                          {drama.genres.join(" · ")} · {drama.status}
                        </span>
                        <span className="mt-1 block text-[11px] text-accent-600">
                          评分 {drama.rating.toFixed(1)}
                        </span>
                      </span>
                    </Link>
                  ))}
                </div>
              </section>
            </div>
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}